import * as React from 'react';

import {
  Button,
  DialogOverlay,
  DialogContent,
  DialogHeader,
} from '@rematter/ui-web';
import { cn } from '@rematter/ui-web/lib';
import { ChevronLeft, ChevronRight } from 'lucide-react';

import { LoadRecord } from '../../types/load';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type PhotoGalleryDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  loads: Array<Partial<LoadRecord> & { id: string }>;
  downloadUrlByLoadId: Record<string, string>;
  initialPhotoId?: string;
};

type PhotoNavigationProps = {
  photos: Array<
    NonNullable<LoadRecord['files']>[number] & {
      loadId: string;
      loadNumber: string;
      url?: string;
    }
  >;
  selectedPhotoIndex: number;
  onPhotoClick: (photoIndex: number) => void;
};

type MainPhotoViewProps = {
  photo:
    | (NonNullable<LoadRecord['files']>[number] & {
        loadId: string;
        loadNumber: string;
        url?: string;
      })
    | undefined;
  onPrevious: () => void;
  onNext: () => void;
  onDownload: () => void;
};

const PhotoNavigation: React.FC<PhotoNavigationProps> = ({
  photos,
  selectedPhotoIndex,
  onPhotoClick,
}) => {
  const t = useTranslations();

  // Group photos by load
  const photosByLoad = React.useMemo(() => {
    const grouped: Record<string, typeof photos> = {};
    photos.forEach((photo) => {
      const loadKey = `${photo.loadNumber}-${photo.loadId}`;
      if (!grouped[loadKey]) {
        grouped[loadKey] = [];
      }
      grouped[loadKey].push(photo);
    });
    return grouped;
  }, [photos]);

  return (
    <div className="@PhotoNavigation w-56 overflow-y-auto border-r border-neutral/20 bg-neutral/5">
      {Object.entries(photosByLoad).map(([loadKey, loadPhotos]) => {
        const [loadNumber] = loadKey.split('-');

        return (
          <div key={loadKey} className="border-neutral/10 p-4">
            <h3 className="mb-3 text-sm font-bold text-neutral/70">
              {t.bookingDetail.photos.dialogue.loadNumber(loadNumber)}
            </h3>
            <div className="flex flex-col gap-2 text-xs font-semibold uppercase">
              {t.bookingDetail.photos.dialogue.loadPhotos}
            </div>
            <div className="my-2 grid grid-cols-2 gap-2">
              {loadPhotos.map((photo) => {
                const globalIndex = photos.findIndex((p) => p.id === photo.id);
                const isSelected = globalIndex === selectedPhotoIndex;

                return (
                  <div
                    key={photo.id}
                    className={cn(
                      'aspect-square cursor-pointer overflow-hidden rounded border-2 bg-neutral/10',
                      isSelected ? 'border-primary/50' : 'border-transparent',
                    )}
                    onClick={() => onPhotoClick(globalIndex)}
                  >
                    {photo.url ? (
                      <img
                        src={photo.url}
                        alt={photo.fileName}
                        className="size-full object-cover"
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center">
                        <span className="text-xs text-neutral/50">
                          {t.shared.noImage}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const MainPhotoView: React.FC<MainPhotoViewProps> = ({
  photo,
  onPrevious,
  onNext,
  // onDownload,
}) => {
  const t = useTranslations();

  if (!photo) {
    return (
      <div className="flex h-[48rem] w-[65.125rem] items-center justify-center">
        <p className="text-neutral/80">{t.shared.noPhotosAvailable}</p>
      </div>
    );
  }

  return (
    <div className="@MainPhotoView flex h-[48rem] w-[65.125rem] flex-col">
      {/* Photo Title */}
      <div className="border-b border-neutral/20 p-4">
        <h3 className="text-lg font-semibold">
          {photo.metadata?.notes || photo.fileName}
        </h3>
        <p className="text-sm text-neutral">
          {t.bookingDetail.photos.dialogue.loadNumber(photo.loadNumber)}
        </p>
      </div>

      {/* Main Photo View */}
      <div className="relative flex flex-1 items-center justify-center bg-neutral/5">
        {photo.url ? (
          <img
            src={photo.url}
            alt={photo.fileName}
            className="max-h-[48rem] max-w-[65.125rem] object-contain"
          />
        ) : (
          <div className="text-center text-neutral/80">
            <p>{t.bookingDetail.photos.noPhotos}</p>
          </div>
        )}

        {/* Navigation Controls */}
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 transform gap-2">
          <Button
            variant="ghost"
            size="icon"
            icon={<ChevronLeft />}
            onPress={onPrevious}
            className="bg-white/90 shadow-sm hover:bg-white"
          />
          <Button
            variant="ghost"
            size="icon"
            icon={<ChevronRight />}
            onPress={onNext}
            className="bg-white/90 shadow-sm hover:bg-white"
          />
        </div>
      </div>
    </div>
  );
};

export const PhotoGalleryDialog: React.FC<PhotoGalleryDialogProps> = ({
  isOpen,
  onClose,
  loads,
  downloadUrlByLoadId,
  initialPhotoId,
}) => {
  const t = useTranslations();

  // Flatten all photos from all loads
  const allPhotos = React.useMemo(() => {
    const photos: Array<
      NonNullable<LoadRecord['files']>[number] & {
        loadId: string;
        loadNumber: string;
        url?: string;
      }
    > = [];

    loads.forEach((load) => {
      load.files?.forEach((file) => {
        if (file.id) {
          photos.push({
            ...file,
            loadId: load.id,
            loadNumber: load.loadNumber || '',
            url: downloadUrlByLoadId[file.id],
          });
        }
      });
    });

    return photos;
  }, [loads, downloadUrlByLoadId]);

  // Find initial photo index
  const initialPhotoIndex = React.useMemo(() => {
    if (!initialPhotoId) return 0;
    return allPhotos.findIndex((photo) => photo.id === initialPhotoId);
  }, [allPhotos, initialPhotoId]);

  const [selectedPhotoIndex, setSelectedPhotoIndex] = React.useState(
    initialPhotoIndex >= 0 ? initialPhotoIndex : 0,
  );

  // Update selected photo when initialPhotoId changes
  React.useEffect(() => {
    if (initialPhotoIndex >= 0) {
      setSelectedPhotoIndex(initialPhotoIndex);
    }
  }, [initialPhotoIndex]);

  const selectedPhoto = allPhotos[selectedPhotoIndex];

  const handlePreviousPhoto = () => {
    setSelectedPhotoIndex((prev) =>
      prev > 0 ? prev - 1 : allPhotos.length - 1,
    );
  };

  const handleNextPhoto = () => {
    setSelectedPhotoIndex((prev) =>
      prev < allPhotos.length - 1 ? prev + 1 : 0,
    );
  };

  const handlePhotoClick = (photoIndex: number) => {
    setSelectedPhotoIndex(photoIndex);
  };

  const handleDownload = () => {
    if (selectedPhoto?.url) {
      window.open(selectedPhoto.url, '_blank');
    }
  };

  if (!isOpen) return null;

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onClose}
      isDismissable={true}
      className="@PhotoGalleryDialog"
    >
      <DialogContent className="h-[44rem] w-[88rem] overflow-hidden p-0">
        <DialogHeader>
          <div className="text-xl font-semibold">
            {t.bookingDetail.photos.dialogue.title}
          </div>
        </DialogHeader>

        <div className="flex h-full">
          <PhotoNavigation
            photos={allPhotos}
            selectedPhotoIndex={selectedPhotoIndex}
            onPhotoClick={handlePhotoClick}
          />

          <MainPhotoView
            photo={selectedPhoto}
            onPrevious={handlePreviousPhoto}
            onNext={handleNextPhoto}
            onDownload={handleDownload}
          />
        </div>
      </DialogContent>
    </DialogOverlay>
  );
};
